import {Component, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {MatSnackBar} from "@angular/material/snack-bar";
import {environment} from "@env/environment";
import {BillingDetailsUploadService} from "@features/leasing/billing-details-upload/billing-details-upload.service";
import {Lease} from "@features/leasing/lease-upload/lease-upload.model";
import {AddressService, RetailerAddress} from "@ui-kit/utils/service/address.service";
import {LoadingService} from "@ui-kit/utils/service/loading.service";
import {CrudTableComponent} from "@ui-webkit/components/crud-table/crud-table.component";
import {CsvImportComponent} from "@ui-webkit/components/csv-import/csv-import.component";
import {UploadDataModel, UploadResponse} from "@ui-webkit/components/csv-import/csv-import.model";
import {
  bankAccountTypes, bankListWithDefaultBranchCode,
  BatchRetailerResponse,
  billingDetailsUploadModel,
  BillingResponse,
  BillingUpload,
  RetailerBillingSummary
} from "./billing-details-upload.model";

@Component({
  selector: 'app-billing-details-upload',
  standalone: true,
  imports: [
    CsvImportComponent,
    CrudTableComponent
  ],
  templateUrl: './billing-details-upload.component.html',
  styleUrl: './billing-details-upload.component.scss'
})
export class BillingDetailsUploadComponent implements OnInit, OnChanges {
  @ViewChild('csvImport') csvImport!: CsvImportComponent;
  @Input() parentLease!: Lease;

  private _loader = inject(LoadingService);
  protected loading$ = this._loader.loading$;

  uploadConfig: UploadDataModel[] = billingDetailsUploadModel;
  uploadEndpoint = this.baseUrl;
  validateEndpoint = this.baseUrl + '/validate';
  otpEndpoint = this.baseUrl + '/otp';
  disableUpload = true;

  subLeaseDetails: RetailerBillingSummary[] = [];
  addressMap = new Map<string, RetailerAddress>();

  constructor(private billingDetailsUploadService: BillingDetailsUploadService,
              private addressService: AddressService,
              private snackBar: MatSnackBar) {
    this.uploadConfig.find(f => f.property == 'bankingAccountType')!.computeMethod = this.setBankAccountType.bind(this);
    this.uploadConfig.find(f => f.property == 'bankingBranchCode')!.computeMethod = this.setBranchCode.bind(this);
    this.uploadConfig.find(f => f.property == 'bankingMethodType')!.computeMethod = this.setBankingMethodType.bind(this);
    this.uploadConfig.find(f => f.property == 'address')!.computeMethod = this.setAddress.bind(this);
    this.uploadConfig.find(f => f.property == 'addressPlace')!.computeMethod = this.setAddressPlace.bind(this);
  }

  ngOnInit() {
    this.loadSubLeaseDetails();
    this.addressService.loadGoogleMapsAPI(environment);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['parentLease']) {
      this.loadSubLeaseDetails();
    }
  }

  loadSubLeaseDetails() {
    this.billingDetailsUploadService.getSubLeaseDetails(this.parentLease.uuid).subscribe({
      next: data => {
        this.subLeaseDetails = data;
      },
      error: err => {
        console.log(err);
      }
    })
  }

  setBankAccountType(data: any) {
    const type = bankAccountTypes.find(f => f.type == data['bankingAccountTypeCode']);
    if (type) return type.code;
    return null;
  }

  setBranchCode(data: any) {
    const type = bankListWithDefaultBranchCode.find(f => f.bankName == data['bankingBankName']);
    if (type) return type.centralBranchCode;
    return null;
  }

  setAddress(data: any) {
    const formattedAddress = data['billingFormattedAddress'];
    if (this.addressMap.has(formattedAddress)) {
      return this.addressMap.get(formattedAddress)!.address;
    }
    return null;
  }

  setAddressPlace(data: any): any {
    const formattedAddress = data['billingFormattedAddress'];
    if (this.addressMap.has(formattedAddress)) {
      return this.addressMap.get(formattedAddress)!.addressPlace;
    }
    return null;
  }

  setBankingMethodType(data: any) {
    return 'TRANSACT_PAYBACK';
  }

  setAddressMap(data: any[]) {
    if (this.addressMap.size == 0) {
      const addressPopulated = data[0]['billingFormattedAddress'] != null;
      if (addressPopulated) {
        data.forEach(line => {
          const formattedAddress = line['billingFormattedAddress'];
          this.addressService.geocodeAddressFromFormattedString(formattedAddress).then(addressObject => {
            if (addressObject) {
              const parsedAddress = this.addressService.parseJsonAddressData(addressObject.location, addressObject.place);
              this.addressMap.set(formattedAddress, parsedAddress);
            }
          }).catch(error => {
            console.error('Failed to fetch address:', error);
          });
        })
      }
    }
  }

  updateDataAfterValidation(data: { req: BillingUpload[], res: BatchRetailerResponse[] }) {
    const dataToUpload = [...data.req];
    dataToUpload.forEach(line => {
      const resForLine = data.res.find(f => f.retailerCompanyName == line.retailerCompanyName);
      line.errorMessages = [];
      if (resForLine) {
        line.retailerId = resForLine.retailerId;
        if (resForLine.errorMessages && resForLine.errorMessages.length > 0) {
          line.errorMessages.push(...resForLine.errorMessages);
        }
      }
    })

    const allErrors = dataToUpload.flatMap(m => m.errorMessages)
      .filter(f => !!f);
    this.disableUpload = allErrors.length > 0
    this.csvImport.overwriteData(dataToUpload);
  }

  updateAfterUpload(data: { req: BillingUpload[], res: UploadResponse }) {
    if (data.res.errorsMessages && data.res.errorsMessages.length > 0) {
      data.res.errorsMessages.forEach(error => this.snackBar.open(error));
    } else {
      this.csvImport.closeOtpDialog();
      this.loadSubLeaseDetails();
      this.csvImport.fileSuccessfullyUploaded(data.req, data.res);
    }
  }

  get baseUrl() {
    return environment.apiRetailersHost + `/console/manage-retailer/batch/billing-and-banking`
  }

  protected readonly retailerBillingSummaryModel = RetailerBillingSummary;
}
